






























































import { Component, Vue, Watch } from 'vue-property-decorator';
import { ICalendar, IClientCalendar, IClientCalendarSession, IValidations, ICalendarSession, IPriceCategory } from '@/interfaces';
import { AjaxHelper } from '@/AjaxHelper';
import { CalendarSessionValidator, CalendarValidator } from '@/validators';

import Field from '@/components/Field.vue';
import TextField from '@/components/TextField.vue';
import Btn from '@/components/Btn.vue';
import List from '@/components/List.vue';
import ListItem from '@/components/ListItem.vue';
import TextareaField from '@/components/TextareaField.vue';
import VSelect from 'vue-select';

function blankSession(): IClientCalendarSession { return { name: '', date: null, purchase: null }; }

/**
 * We need to convert what is coming down from the server into a workable format for the client, this is because we are using
 * a custom range date picker and it requires a specific format for the range params
 * 
 * @param calendar The server calendar we need to process
 */
function convertToClient(calendar: ICalendar): IClientCalendar {
  return {
    id: calendar.id,
    name: calendar.name,
    description: calendar.description,
    priceCategoryId: calendar.priceCategoryId,
    sessions: calendar.sessions?.map(x => {
      return {
        name: x.name,
        date: x.startDate && x.endDate ? { start: new Date(x.startDate), end: new Date(x.endDate) } : null,
        purchase: x.purchaseStart && x.purchaseEnd ? { start: new Date(x.purchaseStart), end: new Date(x.purchaseEnd) } : null
      };
    }) ?? []
  }
}

@Component({
  name: 'admin-calendar-page',
  components: { Field, TextField, List, ListItem, Btn, VSelect, TextareaField },
  beforeRouteEnter: async (to, from, next) => {
    const categories = await AjaxHelper.get('/pricecategory');
    let model: IClientCalendar = { name: '', description: '', priceCategoryId: null, sessions: [] };
    let isNew = true;

    if (to.params.id && to.params.id !== 'new') {
      const fromServer = await AjaxHelper.get<ICalendar>(`/calendar/${to.params.id}`);
      model = convertToClient(fromServer);
      isNew = false;
    }

    next(vm => {
      (vm as any).categories = categories;
      (vm as any).model = model;
      (vm as any).isNew = isNew;
    });
  }
})
export default class AdminCalendarComponent extends Vue {
  public isNew = true;
  public saving = false;
  public model: IClientCalendar = { name: '', description: '', priceCategoryId: null, sessions: [] };

  public modelShowValidations = false;
  public modelValidations: IValidations<IClientCalendar> | null = null;
  public get sessionValidations(): (IValidations<IClientCalendarSession> | null)[] { return this.model.sessions?.map(x => this.modelShowValidations ? this.generateValidations(x) : null) ?? []; }

  public sessionValidator = new CalendarSessionValidator();
  public calendarValidator = new CalendarValidator();

  public categories: IPriceCategory[] = [];

  public get emptySessionsValidation(): string[] {
    if (this.modelValidations !== null && this.modelValidations.sessions !== undefined) {
      return ['border-red-800', 'bg-red-100', 'text-red-800'];
    }
    return ['border-blue-800', 'bg-blue-100', 'text-blue-800']
  }

  public get emptySessionsText(): string {
    const validations = this.getValidationText(this.modelValidations, 'sessions');
    if (validations.length > 0) {
      return validations[0];
    }
    return 'No Sessions Found';
  }
  
  public addSession() {
    if (this.model.sessions !== undefined) {
      this.model.sessions.push(blankSession());
    }
  }

  public removeSession(item: IClientCalendarSession) {
    const index = this.model.sessions?.indexOf(item) ?? -1;
    if (index > -1 && this.model.sessions !== undefined) {
      this.model.sessions.splice(index, 1);
    }
  }

  public async save() {
    try {
      this.saving = true;
      const validations = this.calendarValidator.isValid(this.model);
      if (validations === true) {
        this.modelShowValidations = false;
        this.modelValidations = null;

        if (this.isNew) {
          const model = await AjaxHelper.post<ICalendar, ICalendarSession>('/calendar', this.convertToServer(this.model));
          this.model = convertToClient(model);
          this.isNew = false;

          this.$alert.success('Successfully Created Calendar');
        } else {
          const model = await AjaxHelper.put<ICalendar, ICalendarSession>(`/calendar/${this.model.id}`, this.convertToServer(this.model));
          this.model = convertToClient(model);

          this.$alert.success('Successfully Updated Calendar');
        }
      } else {
        this.modelValidations = validations;
        this.modelShowValidations = true;
      }
    } catch {
      this.$alert.danger('Error Occured While Saving Calendar');
    } finally {
      this.saving = false;
    }
  }

  /**
   * Helper method to get the validation text that should be displayed to the user when the validation is triggered
   * 
   * @param key The key we want to get the validations from
   */
  public getValidationText<T>(validations: IValidations<T> | null, key: keyof T) {
    return (validations !== null ? validations[key] : null) ?? '';
  }

  /**
   * Helper method is meant to watch the model and make updates based on if the model can be saved
   * 
   * @param newModel The model that has been updated
   */
  @Watch('model', { deep: true })
  public onModelChange(newModel: IClientCalendar) {
    if (this.modelShowValidations) {
      const validations = this.calendarValidator.isValid(newModel);
      this.modelValidations = validations === true ? null : validations;
    }
  }

  /**
   * Helper method to generate validations and return null or the validations for use by the component
   * 
   * @param session The current session we are handling
   */
  private generateValidations(session: IClientCalendarSession) {
    const validations = this.sessionValidator.isValid(session);
    return validations === true ? null : validations;
  }

  /**
   * We need to convert the client calendar into the what the server is expecting since the date range component requires
   * a specific format
   * 
   * @param calendar The client verion of this calendar
   */
  private convertToServer(calendar: IClientCalendar): ICalendar {
    return {
      id: calendar.id,
      name: calendar.name,
      description: calendar.description,
      priceCategoryId: calendar.priceCategoryId,
      sessions: calendar.sessions?.map(x => ({
        name: x.name,
        startDate: x.date?.start.toISOString() ?? null,
        endDate: x.date?.end.toISOString() ?? null,
        purchaseStart: x.purchase?.start.toISOString() ?? null,
        purchaseEnd: x.purchase?.end.toISOString() ?? null
      }))
    };
  }
}
